import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import { useOnSuccessActionModal } from 'components/inbox/hooks/useOnSuccessActionModal';
import { useState } from 'react';
import useModal from 'components/remodal/v2/useModal';
import MultiBookModal from 'components/modals/MultiBookModal';
import { BookingActionButtonProps } from 'components/ui/BookingActionsBox/BookingActionButton';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import { useUserDetails } from 'auth/mutations/userHooks';
import BookingActionsBox from 'components/ui/BookingActionsBox/BookingActionsBox';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import OwnerBookingActionsModal from '../../../../../pages/a/bookings/[bookingId]/owner_action/[action]';
import CaptainBookingActionsModal from '../../../../../pages/a/bookings/[bookingId]/captain_action/[action]';
import { MESSAGE_TYPES } from '../constants';
import { SpecialMessageProps } from './types';
import { approveDeclineBookingActions as approveDeclineBookingAction } from './actions';

export default function BookNowBoxActions({
  msg,
  booking,
  openEditBookingModal,
}: SpecialMessageProps & { openEditBookingModal: () => void }) {
  const { enqueueSnackbar } = useLazySnackbar();
  const { userId } = useUserDetails();
  const router = useRouter();
  const rudderTrack = useRudderStackTrack();

  const canAcceptDecline =
    (booking?.user_type === 'owner' && booking?.state === 'payment_held' && booking?.can_approve) ||
    (booking?.user_type === 'captain' &&
      booking?.captain?.id === userId &&
      booking?.captain_state === 'pending' &&
      booking?.can_captain_approve);

  const isCaptain = booking?.user_type === 'captain' && booking?.captain?.id === userId;

  const onSuccess = useOnSuccessActionModal();

  const [ownerBookingActionType, setOwnerBookingActionType] = useState<'approve' | 'decline'>('approve');
  const { modal: ownerBookingActionsModal, openModal: openOwnerBookingActionsModal } = useModal(
    <OwnerBookingActionsModal
      booking={booking}
      openEditBookingModal={openEditBookingModal}
      actionType={ownerBookingActionType}
      onSuccess={onSuccess}
    />,
    {
      size: ownerBookingActionType === 'decline' ? 'medium' : 'large',
      overridePadding: ownerBookingActionType === 'decline' ? 'small' : undefined,
      trackingName: 'owner_booking_actions_modal',
    }
  );
  const handleOpenOwnerBookingActionsModal = (actionType: 'approve' | 'decline') => {
    setOwnerBookingActionType(actionType);
    openOwnerBookingActionsModal();
  };

  const [captainActionType, setCaptainActionType] = useState<'approve' | 'decline'>('decline');
  const { modal: captainBookingActionsModal, openModal: openCaptainBookingActionsModal } = useModal(
    <CaptainBookingActionsModal booking={booking} actionType={captainActionType} onSuccess={onSuccess} />,
    {
      size: 'medium',
      overridePadding: 'small',
      trackingName: 'captain_booking_actions_modal',
    }
  );
  const handleOpenCaptainActionModal = (actionType: 'approve' | 'decline') => {
    setCaptainActionType(actionType);
    openCaptainBookingActionsModal();
  };

  const { modal: multibookModal, openModal: openMultibookModal } = useModal(
    <MultiBookModal booking={booking} actionType="approve" />,
    {
      size: 'small',
      trackingName: 'multibook_modal',
    }
  );

  const approveDeclineBookingActions = approveDeclineBookingAction(
    booking,
    router,
    enqueueSnackbar,
    handleOpenOwnerBookingActionsModal,
    handleOpenCaptainActionModal,
    openMultibookModal
  );

  const title = (
    <span>
      {booking?.renter?.first_name}{' '}
      {isCaptain ? 'has requested you to captain their booking' : 'has requested to book your boat'}
    </span>
  );

  const userType = isCaptain ? 'captain' : 'owner';

  const expiresAt = dayjs(booking.request_expires_at).format('MMM D, YYYY • h:mmA');

  const fireTracking = (action: 'approve' | 'decline' | 'openEditBookingModal') => {
    rudderTrack('booking_action_owner', {
      bookingId: booking.id,
      bookingState: booking.state,
      action,
      location: 'conversation_action_box',
    });
  };

  return (
    <>
      {
        // @ts-ignore
        msg.type === MESSAGE_TYPES.bookNow && canAcceptDecline && !msg?.booking_modification && (
          <BookingActionsBox
            title={title}
            ctaText="Approve"
            userType={userType}
            onSubmit={() => {
              fireTracking('approve');
              approveDeclineBookingActions.approve();
            }}
            showCloseButton={false}
            showSubtitle
            subtitle={`This booking request will expire at ${expiresAt}`}
            ctas={
              [
                {
                  ctaText: 'Decline',
                  userType: isCaptain ? 'captain_outline' : 'owner_outline',
                  onSubmit: () => {
                    fireTracking('decline');
                    approveDeclineBookingActions.decline();
                  },
                } as BookingActionButtonProps,
                booking.can_modify &&
                  ({
                    ctaText: 'Edit booking',
                    userType: isCaptain ? 'captain' : 'owner_outline',
                    onSubmit: () => {
                      fireTracking('openEditBookingModal');
                      openEditBookingModal();
                    },
                    justText: true,
                  } as BookingActionButtonProps),
              ].filter(Boolean) as BookingActionButtonProps[]
            }
          />
        )
      }
      {ownerBookingActionsModal}
      {captainBookingActionsModal}
      {multibookModal}
    </>
  );
}
